import * as React from 'react';
import { ChevronRight } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import type { ColumnTransformSuggestion } from '@ooh/contracts';
import type {
  ColumnMappingItem,
  FieldOptionItem,
  MappingPhaseLabels,
  SupplierOption,
} from './mapping-phase-types';
import {
  MappingSupplierSelect,
  MediaTypeOverridePicker,
  MappingErrorBanner,
} from './mapping-phase-parts';
import {
  MappingTableRow,
  MappingRequiredSections,
  TypeOverrideAutoRow,
} from './mapping-phase-table';
import { MappingPatternEditorSlot } from './mapping-phase-transform-editor';
import { MappingValueMapEditorSlot } from './mapping-phase-value-map-editor';
import {
  MissingRequiredFieldsSection,
  MappingConstantEditorSlot,
} from './mapping-phase-constant-editor';
import { MappingPhaseSourceSection } from './mapping-phase-source-bar-section';
import { MappingPhasePreviewSection } from './mapping-phase-preview-section';
import { useMappingPhaseState } from './use-mapping-phase-state';
import { MEDIA_TYPE_FIELD_KEY, collectMappedFieldKeys } from '../lib/collect-mapped-field-keys';

export type {
  ColumnMappingItem,
  FieldOptionItem,
  MappingPhaseLabels,
  SupplierOption,
} from './mapping-phase-types';

export interface MappingPhaseProps {
  columnMappings: ColumnMappingItem[];
  fieldOptions: FieldOptionItem[];
  suppliers: SupplierOption[];
  /** Pre-filled supplier name (from file or user input) */
  supplierName?: string;
  /** When true show "saved mapping applied" banner instead of save section */
  hasSavedMapping: boolean;
  /** Field keys required by the system but entirely absent from the supplier file */
  missingRequiredFieldKeys?: string[];
  labels: MappingPhaseLabels;
  onNext: (mappings: ColumnMappingItem[], saveForSupplierId?: string) => void;
  /** Called when the user selects a supplier in the combobox — lets the parent refetch columns */
  onSupplierChange?: (supplierId: string | null) => void;
  /** Inline error message (e.g. from a failed confirm-mapping call) */
  errorMessage?: string | null;
  /** Adapter-driven column lists surfaced when mediaTypeOverride is set. When provided,
   * fieldOptions are pre-filtered upstream in MappingPhaseSection to this adapter's columns. */
  adapterColumns?: { required: string[]; optional: string[] };
  /** Current per-file media type override; collapses the Typ nośnika row with an auto note. */
  mediaTypeOverride?: string;
  /** Called when user picks a type or clears the override. */
  onMediaTypeOverrideChange?: (value: string | null) => void;
  /** Non-empty sample values per detected source column, from the columns API response. */
  columnSamples?: Record<string, string[]>;
  /** Full distinct non-empty values per detected source column, for value-map editing. */
  columnDistinctValues?: Record<string, string[]>;
  /** Advisory pattern-transform suggestions per detected column, used to fuzzy pre-fill the pattern editor. */
  transformSuggestions?: ColumnTransformSuggestion[];
  /** The un-applied auto-suggested mapping — restored when the operator clears a recognized saved config. */
  manualFallbackMapping?: ColumnMappingItem[];
  /** Uploaded file's display name, for the source bar. */
  fileName?: string;
  fileSizeKb?: number;
  /** Estimated total data-row count (from the job's totalRows). */
  estimatedRows?: number;
  /** Workbook sheet names; the sheet picker only renders when length > 1. */
  sheets?: string[];
  selectedSheet?: string;
  /** Called when the operator picks a different sheet — parent refetches columns for it. */
  onSheetChange?: (sheet: string) => void;
  /** 0-based detected header row index, for the auto-detected header note. */
  headerRowIndex?: number;
}

export function MappingPhase(props: MappingPhaseProps) {
  const {
    fieldOptions,
    suppliers,
    hasSavedMapping,
    labels,
    onSupplierChange,
    errorMessage,
    mediaTypeOverride,
    onMediaTypeOverrideChange,
    columnSamples,
    columnDistinctValues,
    fileName,
    fileSizeKb,
    estimatedRows,
    sheets,
    selectedSheet,
    onSheetChange,
  } = props;
  const state = useMappingPhaseState(props);

  // When a source column is already mapped directly to the media-type field, type is resolved
  // per row from that column — the file-level override picker would be redundant/confusing
  // until the operator actually activates an override (which must still be clearable).
  // Uses collectMappedFieldKeys (the shared "which FIELD_OPTIONS keys does this mapping
  // resolve to" helper, see collect-mapped-field-keys.ts) rather than a plain `mappedTo`
  // check, so a column that resolves `type` via a pattern transform's per-blank targets is
  // also covered — matching every other required-field-coverage check in this feature.
  const hasTypeColumnMapped = collectMappedFieldKeys(state.mappings).has(MEDIA_TYPE_FIELD_KEY);
  const showTypeOverridePicker =
    Boolean(onMediaTypeOverrideChange) && (mediaTypeOverride != null || !hasTypeColumnMapped);

  // 1-based position of each source column in the file, keyed by mapping id — shown next to the
  // column name so operators can tell apart duplicate source-column headers (e.g. two "Wesele"
  // columns), since columnSamples/columnDistinctValues are keyed by column NAME and would
  // otherwise silently collide.
  const columnNumberById = new Map(state.mappings.map((m, idx) => [m.id, idx + 1]));

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="phase-mapping">
      {/* Inline error from confirmMapping mutation */}
      <MappingErrorBanner errorMessage={errorMessage} />

      {/* Supplier select — always visible at the top of the form (reuses the
       * availability-import LabeledSelectField pattern, see supplier-mapping-fields.tsx),
       * hidden only while a recognized saved config is applied and re-shown once that saved
       * config is cleared (state.effectiveHasSavedMapping, Bug 5/86can7jwy: the only UI with
       * a supplier-constant input must come back once the operator clears the saved config,
       * or "Dalej" 400s on a missing supplier with no visible affordance). */}
      {!state.effectiveHasSavedMapping && (
        <MappingSupplierSelect
          suppliers={suppliers}
          selectedSupplierId={state.selectedSupplierId}
          onSupplierSelect={(id) => {
            state.setSelectedSupplierId(id);
            // Bug (data loss): once the operator has cleared the recognized saved config,
            // this select only decides which supplier the CURRENT (possibly manually
            // edited) mapping is saved under on Next — it must never again propagate to
            // the parent's onSupplierChange, which refetches columns for that supplier and
            // remounts MappingPhase with a fresh key, silently re-seeding columnMappings
            // from that supplier's old saved config over the operator's live edits.
            if (!state.savedConfigCleared) {
              onSupplierChange?.(id);
            }
          }}
          labels={labels}
        />
      )}

      {/* Source file summary + sheet picker + consolidated recognized-layout/fallback banner */}
      <MappingPhaseSourceSection
        hasSavedMapping={hasSavedMapping}
        savedConfigCleared={state.savedConfigCleared}
        onClearSavedConfig={state.handleClearSavedConfig}
        nonStandardBanner={labels.nonStandardBanner}
        autoSuggestLabels={state.autoSuggestLabels}
        fileName={fileName}
        fileSizeKb={fileSizeKb}
        estimatedRows={estimatedRows}
        sheets={sheets}
        selectedSheet={selectedSheet}
        onSheetChange={onSheetChange}
        sourceBarLabels={state.sourceBarLabels}
      />

      {/* Per-file media type override picker — hidden once a column is directly mapped to the
       * type field and no override is active yet (see hasTypeColumnMapped above). */}
      {Boolean(showTypeOverridePicker) && (
        <MediaTypeOverridePicker
          mediaTypeOverride={mediaTypeOverride}
          onChange={onMediaTypeOverrideChange!}
          labels={{
            typeOverrideLabel: labels.typeOverrideLabel,
            typeOverridePlaceholder: labels.typeOverridePlaceholder,
          }}
        />
      )}

      {/* Mapping table */}
      <div className="rounded-lg border border-border overflow-hidden" data-testid="mapping-table">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground w-[220px]">
                {labels.columnHeaderSource}
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                {labels.columnHeaderTarget}
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Static "auto: file-level override" row shown when Typ nośnika row is collapsed */}
            {Boolean(state.hasCollapsedTypRow) && (
              <TypeOverrideAutoRow autoNoteLabel={labels.typeOverrideAutoNote} />
            )}
            {state.visibleMappings.map((mapping) => (
              <MappingTableRow
                key={mapping.id}
                mapping={mapping}
                columnNumber={columnNumberById.get(mapping.id) ?? 0}
                fieldOptions={fieldOptions}
                groupedOptions={state.groupedOptions}
                skipLabel={labels.skipColumnOption}
                unknownColumnLabel={labels.unknownColumnLabel}
                fieldSearchPlaceholder={labels.fieldSearchPlaceholder}
                fieldNoResults={labels.fieldNoResults}
                onMappingChange={state.handleMappingChange}
                sampleValues={columnSamples?.[mapping.sourceColumn] ?? []}
                onEditPattern={state.handleEditPattern}
                onBlankTargetChange={state.handleBlankTargetChange}
                patternLabels={state.transformLabels}
              />
            ))}
            {state.visibleMappings.length === 0 && !state.hasCollapsedTypRow && (
              <tr>
                <td colSpan={2} className="px-4 py-6 text-center text-[13px] text-muted-foreground">
                  —
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Required field error sections */}
      <MappingRequiredSections
        unmappedRequiredFields={state.unmappedRequiredFields}
        labels={labels}
      />
      <MissingRequiredFieldsSection
        missingRequiredFields={state.missingRequiredFields}
        constantAssignments={state.constantAssignments}
        labels={state.missingRequiredLabels}
        onEditConstant={state.handleEditConstant}
      />

      {/* Output preview: resolved system columns, post-transform, with validation flags */}
      <MappingPhasePreviewSection
        mappings={state.visibleMappings}
        constantAssignments={state.constantAssignments}
        columnSamples={columnSamples}
        fieldOptions={fieldOptions}
        overrideActive={Boolean(mediaTypeOverride)}
        supplierCovered={state.supplierCoveredByCombobox}
        headingTemplate={labels.previewHeading}
        regionDerivedWarning={labels.previewRegionDerivedWarning}
      />

      {/* Footer action */}
      <div className="flex justify-end pt-2">
        <Button
          type="button"
          onClick={state.handleNext}
          disabled={state.isBlocked}
          data-testid="mapping-next-button"
        >
          {labels.nextButton}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Pattern / value-map editor dialogs for the column currently being edited */}
      <MappingPatternEditorSlot
        editingMapping={state.editingMapping}
        columnSamples={columnSamples}
        fieldOptions={fieldOptions}
        labels={state.transformLabels}
        onPatternChange={state.handlePatternTextChange}
        onBlankTargetChange={state.handleBlankTargetChange}
        onTransformModeChange={state.handleTransformModeChange}
        onClose={() => state.setEditingColumnId(null)}
      />
      <MappingValueMapEditorSlot
        editingMapping={state.editingMapping}
        columnSamples={columnSamples}
        columnDistinctValues={columnDistinctValues}
        labels={state.valueMapLabels}
        onEntryChange={state.handleValueMapEntryChange}
        onTransformModeChange={state.handleTransformModeChange}
        onClose={() => state.setEditingColumnId(null)}
      />

      {/* Constant editor dialog for the missing-required field currently being edited */}
      <MappingConstantEditorSlot
        editingFieldKey={state.editingConstantKey}
        fieldOptions={fieldOptions}
        constantAssignments={state.constantAssignments}
        labels={state.constantLabels}
        mediaTypeAllowedValues={state.constantTypeAllowedValues}
        onChange={state.handleConstantChange}
        onClose={() => state.setEditingConstantKey(null)}
      />
    </div>
  );
}
